import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  type CookieConsent as ConsentType,
  readCookieConsent,
  writeCookieConsent,
} from "@/hooks/use-cookie-consent";
import { updatePostHogConsent } from "@/lib/analytics";

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------

export function CookieConsent() {
  const gpc =
    typeof navigator !== "undefined" &&
    (navigator as unknown as { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true;

  const existing = useMemo(() => readCookieConsent(), []);
  const [showBanner, setShowBanner] = useState<boolean>(() => !existing && !gpc);
  const [openPrefs, setOpenPrefs] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(
    () => existing?.analytics === true && !gpc
  );

  // Listen for external requests to open preferences
  useEffect(() => {
    const onOpen = () => setOpenPrefs(true);
    window.addEventListener("open-cookie-preferences", onOpen);
    return () => window.removeEventListener("open-cookie-preferences", onOpen);
  }, []);

  const acceptAll = () => {
    const consent: ConsentType = {
      analytics: true,
      timestamp: new Date().toISOString(),
      gpc,
    };
    writeCookieConsent(consent);
    updatePostHogConsent();
    setShowBanner(false);
    setOpenPrefs(false);
  };

  const rejectAll = () => {
    const consent: ConsentType = {
      analytics: false,
      timestamp: new Date().toISOString(),
      gpc,
    };
    writeCookieConsent(consent);
    updatePostHogConsent();
    setShowBanner(false);
    setOpenPrefs(false);
  };

  const savePreferences = () => {
    const consent: ConsentType = {
      analytics: analyticsChecked && !gpc,
      timestamp: new Date().toISOString(),
      gpc,
    };
    writeCookieConsent(consent);
    updatePostHogConsent();
    setShowBanner(false);
    setOpenPrefs(false);
  };

  return (
    <>
      {/* Banner */}
      {showBanner && (
        <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4">
          <div className="max-w-4xl mx-auto bg-card/95 backdrop-blur-xl border border-border rounded-2xl p-4 sm:p-5 shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <p className="text-sm text-foreground/90 leading-relaxed sm:flex-1 sm:pr-3">
                We use essential cookies to make the platform work. With your
                consent, we'd also like to enable basic analytics to help us
                improve. You can change your choice anytime.
              </p>
              <div className="flex items-center gap-2 ml-auto sm:shrink-0">
                <button
                  onClick={rejectAll}
                  className="px-3 py-2 rounded-full text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors whitespace-nowrap"
                >
                  Reject
                </button>
                <button
                  onClick={() => setOpenPrefs(true)}
                  className="px-3 py-2 rounded-full text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors whitespace-nowrap"
                >
                  Manage
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 rounded-full text-sm bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  Accept
                </button>
              </div>
            </div>
            {gpc && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Global Privacy Control detected; analytics disabled by default.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {openPrefs && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpenPrefs(false)}
          />
          <div className="relative bg-card border border-border rounded-2xl p-5 sm:p-6 max-w-lg w-full shadow-2xl">
            <button
              onClick={() => setOpenPrefs(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-secondary transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>

            <h3 className="text-lg font-semibold text-foreground">
              Cookie Preferences
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Control optional analytics. Essential cookies are always on for
              security and login.
            </p>

            <div className="mt-5 space-y-4">
              {/* Essential Cookies - Always On */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border bg-background accent-primary cursor-not-allowed"
                  checked
                  disabled
                />
                <label className="text-sm">
                  <span className="font-medium text-foreground">
                    Essential cookies
                  </span>
                  <span className="block text-muted-foreground mt-0.5">
                    Required for the platform to function. Cannot be disabled.
                  </span>
                </label>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
                <input
                  id="analytics-consent"
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border bg-background accent-primary cursor-pointer disabled:cursor-not-allowed"
                  checked={analyticsChecked}
                  disabled={gpc}
                  onChange={(e) => setAnalyticsChecked(e.currentTarget.checked)}
                />
                <label htmlFor="analytics-consent" className="text-sm cursor-pointer">
                  <span className="font-medium text-foreground">
                    Analytics cookies
                  </span>
                  <span className="block text-muted-foreground mt-0.5">
                    Helps us understand feature adoption and performance. No
                    advertising or cross-site tracking.
                  </span>
                </label>
              </div>
            </div>

            {gpc && (
              <p className="mt-3 text-xs text-muted-foreground bg-secondary/50 p-2 rounded-lg">
                🔒 Global Privacy Control is active. Analytics cookies are
                disabled to respect your privacy settings.
              </p>
            )}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={rejectAll}
                className="px-4 py-2 rounded-full text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
              >
                Reject all
              </button>
              <button
                onClick={savePreferences}
                className="px-4 py-2 rounded-full text-sm bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieConsent;
